import React, { useState, useEffect } from 'react';
import { 
  Container, Paper, Typography, Box, TextField, Button, 
  Avatar, Grid, Alert, Divider, CircularProgress 
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../api/services';

const Profile = () => {
  const { user, login } = useAuth(); // login se usa para actualizar el usuario en el contexto
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Las nuevas contraseñas no coinciden' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Contraseña actualizada exitosamente' });
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.old_password?.[0] || 
              error.response?.data?.error || 
              'Error al actualizar la contraseña' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Mi Perfil
      </Typography>

      <Grid container spacing={4}>
        {/* Información del Usuario */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', height: '100%' }}>
            <Avatar
              src={user.profile?.profile_picture}
              alt={user.first_name}
              sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '3rem' }}
            >
              {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
            </Avatar>
            
            <Typography variant="h6" gutterBottom>
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {user.email}
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" display="block" color="text.secondary">
                Rol: {user.profile?.role?.toUpperCase()}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Miembro desde: {new Date(user.date_joined).toLocaleDateString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Cambio de Contraseña */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Seguridad
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Cambiar contraseña
            </Typography>

            {message.text && (
              <Alert severity={message.type} sx={{ mb: 3 }}>
                {message.text}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordChange}>
              <TextField
                fullWidth
                label="Contraseña Actual"
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Nueva Contraseña"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                margin="normal"
                required
                helperText="Mínimo 6 caracteres"
              />
              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                margin="normal"
                required
              />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Actualizar Contraseña'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile;
